import { Box, Grid } from "@mui/material";
import { clearAllCardSelection, setActiveActionCard, setOpenSections } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Feedback from "components/common/Feedback";
import PlanType from "components/common/PlanType";
import UrlTabs from "components/common/UrlTabs";
import { uniq } from "lodash";
import React, { useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "styles/Tokens";
import { ABOVE_PROPERTY, brandDeadLine, messages } from "utils/CommonConstants";
import { planTypes, responseTypes } from "utils/Types";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import AccessBanner from "../../common/AccessBanner/PlanDetailAccessBanner";
import ActionCardDialogsContext from "../ActionCard/Context";
import { updateRecentlyViewedData } from "../Corp/CorpUser.Slice";
import { clearPlanDetail } from "../planDetails/PlanDetail.slice";
import PlanDetailsContent from "../planDetails/PlanDetailsContent";
import {
  clearMyPlanDetails,
  fetchAssigneeDetailsAsync,
  fetchMyPlanDetailAsync,
  setIsRedirectFromNotification
} from "./MyPlanDetails.slice";
import RecognitionsDialog from "./RecognitionsDialog";
import { getTabDetails } from "./TabsConfig";

export default function MyPlanDetails() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: myPlanId, secId } = useParams();

  const selectedMyPlanDetail: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const showLoader: boolean | undefined = useAppSelector((state) => state.myPlanDetails.myPlanDetailShowLoader);
  const { isMobileScreen } = useContext(ThemeContext);
  const { setIsOpenViewActionDialog, setIsOpenEditActionDialog } = React.useContext(ActionCardDialogsContext);
  const { isCorp, isAbovePropertyUser } = useAppSelector((state) => state.app.loggedInUserDetails);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);

  const tabs = useMemo(
    () => getTabDetails(selectedMyPlanDetail, planTypes.MyPlans),
    [selectedMyPlanDetail, planTypes.MyPlans]
  );

  useEffect(() => {
    if (!isCorp && !isAbovePropertyUser) {
      dispatch(
        updateRecentlyViewedData({
          propertyPlan: [
            {
              propertyPlanId: myPlanId
            }
          ]
        })
      );
    }

    return () => {
      dispatch(setActiveActionCard({}));
      dispatch(setIsRedirectFromNotification(false));
      setIsOpenEditActionDialog(false);
      setIsOpenViewActionDialog(false);
      dispatch(clearMyPlanDetails());
    };
  }, []);

  useEffect(() => {
    fetchPlandetails();
  }, [myPlanId]);

  const fetchPlandetails = async () => {
    const res = await dispatch(fetchMyPlanDetailAsync({ id: myPlanId, includePropertyInfo: true }));
    if (res?.payload?.status === responseTypes.not_found) navigate(`/`);
  };

  const deleteCardsMessage = `Modifications can't be made. This is preconfigured to ensure a successful and on-time implementation.`;

  useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (myPlanId != selectedMyPlanDetail.id) return;
    const membersList = uniq([...(selectedMyPlanDetail?.owners || []), ...(selectedMyPlanDetail?.members || [])]);
    const data = {
      value: membersList,
      size: membersList?.length
    };
    dispatch(fetchAssigneeDetailsAsync(data));

    if (selectedMyPlanDetail?.sections?.length) {
      dispatch(setOpenSections({ [secId ?? selectedMyPlanDetail?.sections[0]?.id]: true }));
    }
  }, [dispatch, selectedMyPlanDetail]);

  useEffect(() => {
    dispatch(clearAllCardSelection());
  }, [selectedMyPlanDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearPlanDetail());
    };
  }, []);

  return (
    <>
      <Grid marginX={isMobileScreen ? "-16px" : "unset"}>
        <RecognitionsDialog />
        <Feedback form="updateMyPlanCardDetail" message={messages.editSuccess} />
        <Feedback form="createSection" message="Section Added" />
        <Feedback form="markComplete" message={useAppSelector((state) => state.formDetails["markComplete"]?.details)} />
        <Feedback form="editSection" message={messages.editSuccess} />
        <Feedback form="deleteSection" message="Section Deleted" />
        <Feedback form="deleteActionCard" message="Action Deleted" />
        <Feedback form="deleteMultipleActionCards" message={deleteCardsMessage} />
        <Feedback form="myPlanDetail" />

        {!showLoader && (
          <>
            <Grid
              sx={{
                backgroundColor: tokens.Background.General.Primary,
                padding: isMobileScreen ? "16px" : "24px 24px 40px 32px",
                borderRadius: "12px"
              }}
            >
              <div style={{ marginBottom: "24px" }}>{isCorp && !isImpersonated && <AccessBanner />}</div>
              {selectedMyPlanDetail?.planType && (
                <Box marginBottom={"8px"}>
                  <PlanType
                    planType={selectedMyPlanDetail.planType === brandDeadLine ? 1 : 2}
                    brandDeadlineDate={selectedMyPlanDetail.brandDeadlineDate}
                    initiativeName={selectedMyPlanDetail.initiativeName}
                  />
                </Box>
              )}
              <PlanDetailsContent
                selectedPlanDetail={selectedMyPlanDetail}
                plansType="MyPlans"
                isTemplatePlan={selectedMyPlanDetail?.innCode === ABOVE_PROPERTY}
              />
            </Grid>
            <Grid marginBottom={2.5} marginTop={isMobileScreen ? "48px" : "unset"}>
              <UrlTabs tabs={tabs} />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}
